// src/components/layout/NativeTabBar.jsx
//
// The iOS app's bottom tab bar. Inside the native shell it replaces the web's
// hamburger drawer: the five places people actually go stay one tap away, and
// Log Out lives on the Profile tab instead of below the fold of a drawer.
//
// Pemberton's MobileBottomNav is the approved reference. Layout renders this
// only when Capacitor reports a native platform; the web keeps its header nav.
//
// Australian English throughout.

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, BookOpen, Sparkles, MessageCircle, User } from 'lucide-react';

const TABS = [
  { path: '/dashboard', label: 'Home', icon: LayoutDashboard },
  { path: '/modules', label: 'Dive In', icon: BookOpen },
  { path: '/toolkit', label: 'Toolkit', icon: Sparkles },
  { path: '/companion', label: 'Sofia', icon: MessageCircle },
  { path: '/profile', label: 'Profile', icon: User },
];

const NativeTabBar = () => {
  const { pathname } = useLocation();
  // iOS applies :hover to the last-tapped link, and index.css has a global
  // `a:hover { color: indigo }` that outranks a plain text colour utility. So the
  // active tab needs a hover colour of its own, or a tab you've just tapped turns
  // pale purple instead of gold.
  const isActive = (path) =>
    path === '/dashboard' ? pathname === '/dashboard' : pathname.startsWith(path);

  return (
    <nav
      aria-label="Main"
      className="fixed bottom-0 left-0 right-0 z-50 bg-gunmetal/95 backdrop-blur-md border-t border-bright-gold/20 pb-[env(safe-area-inset-bottom)]"
    >
      <ul className="flex items-stretch justify-around h-16">
        {TABS.map(({ path, label, icon: Icon }) => {
          const active = isActive(path);
          return (
            <li key={path} className="flex-1">
              <Link
                to={path}
                aria-current={active ? 'page' : undefined}
                className={`flex h-full min-h-[44px] flex-col items-center justify-center gap-1 transition-colors ${
                  active ? 'text-bright-gold hover:text-bright-gold' : 'text-pearl/70 hover:text-pearl'
                }`}
              >
                <Icon className="w-5 h-5" aria-hidden="true" />
                <span className="text-[11px] font-medium leading-none">{label}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default NativeTabBar;
